import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, getErrorMessage } from '../services/api';
import { RunnerType, CreateRunnerRequest, UpdateRunnerRequest, ExchangeCredential, User } from '../types';
import { CodeEditor, getDefaultScript } from '../components/CodeEditor';

const STRATEGY_OPTIONS = [
  { value: 'DCARunner', label: 'DCA (Dollar Cost Averaging)', type: 'Spot' as RunnerType, isScripted: false },
  { value: 'TrailingGridRunner', label: 'Trailing Grid', type: 'Spot' as RunnerType, isScripted: false },
  { value: 'TrendFollowingRunner', label: 'Trend Following', type: 'Futures' as RunnerType, isScripted: false },
  { value: 'ScriptedSpotRunner', label: 'Scripted (Spot)', type: 'Spot' as RunnerType, isScripted: true },
  { value: 'ScriptedFuturesRunner', label: 'Scripted (Futures)', type: 'Futures' as RunnerType, isScripted: true },
];

export function RunnerForm() {
  const { runnerId } = useParams<{ runnerId: string }>();
  const navigate = useNavigate();
  const isEdit = !!runnerId;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const [displayName, setDisplayName] = useState('');
  const [className, setClassName] = useState('DCARunner');
  const [symbol, setSymbol] = useState('');
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [startingInvestment, setStartingInvestment] = useState('');
  const [runnerType, setRunnerType] = useState<RunnerType>('Spot');
  const [intervalHours, setIntervalHours] = useState('');
  const [configJson, setConfigJson] = useState('');
  const [scriptCode, setScriptCode] = useState('');
  const [exchangeCredentialId, setExchangeCredentialId] = useState<number | null>(null);
  const [credentials, setCredentials] = useState<ExchangeCredential[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [ownerId, setOwnerId] = useState<number | null>(null);

  const isScriptedRunner = className === 'ScriptedSpotRunner' || className === 'ScriptedFuturesRunner';

  const loadCredentials = useCallback(async () => {
    try {
      const creds = await api.getExchangeCredentials();
      setCredentials(creds);
      // Auto-select first active credential if none selected
      if (!exchangeCredentialId && creds.length > 0) {
        const activeCred = creds.find(c => c.isActive) || creds[0];
        setExchangeCredentialId(activeCred.exchangeCredentialId);
      }
    } catch (err) {
      console.error('Failed to load credentials:', err);
    }
  }, [exchangeCredentialId]);

  const loadRunner = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      const runner = await api.getRunner(parseInt(id));
      setDisplayName(runner.displayName);
      setClassName(runner.className);
      setSymbol(runner.symbol);
      setBaseCurrency(runner.baseCurrency);
      setStartingInvestment(runner.startingInvestment.toString());
      setRunnerType(runner.runnerType);
      setIntervalHours(runner.intervalHours?.toString() || '');
      setConfigJson(runner.configJson || '');
      setScriptCode(runner.scriptCode || '');
      setExchangeCredentialId(runner.exchangeCredentialId);
      setOwnerId(runner.userId);
    } catch (err: unknown) {
      setError('Failed to load runner');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCredentials();
    if (isEdit) {
      api.getUsers()
        .then(setUsers)
        .catch(() => { /* non-admin users won't have access - that's ok */ });
    }
  }, [isEdit]);

  useEffect(() => {
    if (isEdit && runnerId) {
      void loadRunner(runnerId);
    }
  }, [isEdit, runnerId, loadRunner]);

  const handleStrategyChange = (value: string) => {
    setClassName(value);
    const strategy = STRATEGY_OPTIONS.find(s => s.value === value);
    if (strategy) {
      setRunnerType(strategy.type);
      // Set default config based on strategy
      if (value === 'TrailingGridRunner' && !configJson) {
        setConfigJson(JSON.stringify({
          GridCount: 5,
          GridSpacingPercent: 2.0,
          TrailTriggerPercent: 5.0,
          MinProfitPercent: 0.5
        }, null, 2));
      } else if (value === 'DCARunner' && !configJson) {
        setConfigJson(JSON.stringify({
          TakeProfitPercent: 10,
          SellPercent: 25,
          BuyDipPercent: 10,
          MinHoursBetweenSells: 1,
          EnableTakeProfit: true,
          EnableBuyDip: true
        }, null, 2));
      }
      // Set default script code for scripted runners
      if (strategy.isScripted && !scriptCode) {
        setScriptCode(getDefaultScript(strategy.type));
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!exchangeCredentialId) {
      setError('Please select an exchange credential');
      return;
    }

    setIsSaving(true);

    const data: CreateRunnerRequest | UpdateRunnerRequest = {
      displayName,
      className,
      symbol,
      baseCurrency,
      startingInvestment: parseFloat(startingInvestment),
      runnerType,
      intervalHours: intervalHours ? parseFloat(intervalHours) : null,
      configJson: configJson || null,
      scriptCode: isScriptedRunner ? (scriptCode || null) : null,
      exchangeCredentialId,
    };

    // Include userId reassignment if changed (edit mode only)
    if (isEdit && ownerId !== null) {
      (data as UpdateRunnerRequest).userId = ownerId;
    }

    const savePromise = isEdit && runnerId
      ? api.updateRunner(parseInt(runnerId), data)
      : api.createRunner(data as CreateRunnerRequest);

    savePromise
      .then(() => {
        void navigate('/');
      })
      .catch((err: unknown) => {
        setError(getErrorMessage(err) || 'Failed to save runner');
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleDelete = () => {
    if (!window.confirm('Are you sure you want to delete this runner?')) {
      return;
    }

    if (!runnerId) return;

    api.deleteRunner(parseInt(runnerId))
      .then(() => {
        void navigate('/');
      })
      .catch((err: unknown) => {
        setError(getErrorMessage(err) || 'Failed to delete runner');
      });
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>{isEdit ? 'Edit Runner' : 'New Runner'}</h1>
          <button onClick={() => { void navigate('/'); }} style={styles.backButton}>
            Back
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.inputGroup}>
            <label style={styles.label}>Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              style={styles.input}
              placeholder="My XRP Strategy"
              required
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Strategy</label>
            <select
              value={className}
              onChange={(e) => handleStrategyChange(e.target.value)}
              style={styles.input}
              required
            >
              {STRATEGY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.type})
                </option>
              ))}
            </select>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Exchange Account</label>
            {credentials.length === 0 ? (
              <div style={styles.noCredentials}>
                No exchange credentials configured.{' '}
                <a href="/settings" style={styles.link}>Add one in Settings</a>
              </div>
            ) : (
              <select
                value={exchangeCredentialId || ''}
                onChange={(e) => setExchangeCredentialId(e.target.value ? parseInt(e.target.value) : null)}
                style={styles.input}
                required
              >
                <option value="">Select exchange account...</option>
                {credentials.map((cred) => (
                  <option key={cred.exchangeCredentialId} value={cred.exchangeCredentialId}>
                    {cred.displayName} ({cred.exchangeName}){cred.isActive ? ' - Active' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {isEdit && users.length > 0 && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Owner</label>
              <select
                value={ownerId || ''}
                onChange={(e) => setOwnerId(e.target.value ? parseInt(e.target.value) : null)}
                style={styles.input}
              >
                {users.map((user) => (
                  <option key={user.userId} value={user.userId}>
                    {user.username}{user.email ? ` (${user.email})` : ''}
                  </option>
                ))}
              </select>
              <span style={styles.hint}>
                Reassigning transfers this bot to the selected user&apos;s account
              </span>
            </div>
          )}

          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Symbol</label>
              <input
                type="text"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                style={styles.input}
                placeholder="XRP-USD"
                required
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Base Currency</label>
              <input
                type="text"
                value={baseCurrency}
                onChange={(e) => setBaseCurrency(e.target.value)}
                style={styles.input}
                placeholder="USD"
                required
              />
            </div>
          </div>

          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Investment Amount</label>
              <input
                type="number"
                value={startingInvestment}
                onChange={(e) => setStartingInvestment(e.target.value)}
                style={styles.input}
                placeholder="100"
                step="0.01"
                min="0"
                required
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Interval (Hours)</label>
              <input
                type="number"
                value={intervalHours}
                onChange={(e) => setIntervalHours(e.target.value)}
                style={styles.input}
                placeholder="24 (optional)"
                step="any"
                min="0.01"
              />
              <span style={styles.hint}>For DCA strategy (e.g., 0.5 = 30 min)</span>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Type</label>
            <select
              value={runnerType}
              onChange={(e) => setRunnerType(e.target.value as RunnerType)}
              style={styles.input}
              required
            >
              <option value="Spot">Spot</option>
              <option value="Futures">Futures</option>
            </select>
          </div>

          {(className === 'TrailingGridRunner') && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Configuration (JSON)</label>
              <textarea
                value={configJson}
                onChange={(e) => setConfigJson(e.target.value)}
                style={{ ...styles.input, ...styles.textarea }}
                placeholder='{"GridCount": 5, "GridSpacingPercent": 2.0}'
                rows={6}
              />
              <span style={styles.hint}>
                Grid config: GridCount, GridSpacingPercent, TrailTriggerPercent, MinProfitPercent
              </span>
            </div>
          )}

          {(className === 'DCARunner') && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Configuration (JSON)</label>
              <textarea
                value={configJson}
                onChange={(e) => setConfigJson(e.target.value)}
                style={{ ...styles.input, ...styles.textarea }}
                placeholder='{"TakeProfitPercent": 10, "SellPercent": 25}'
                rows={8}
              />
              <span style={styles.hint}>
                DCA config: TakeProfitPercent (sell when X% above avg), SellPercent (% of holdings to sell),
                BuyDipPercent (buy back when X% below avg), MinHoursBetweenSells, EnableTakeProfit, EnableBuyDip
              </span>
            </div>
          )}

          {isScriptedRunner && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Strategy Script (JavaScript)</label>
              <CodeEditor
                value={scriptCode}
                onChange={setScriptCode}
                height="500px"
              />
              <span style={styles.hint}>
                Available API: api.Buy(qty), api.Sell(qty), api.BuyAmount(dollars), api.LimitBuy(qty, price),
                api.LimitSell(qty, price), api.GetBalance(), api.GetPrice(), api.Log(msg), api.SaveState()
                {runnerType === 'Futures' && (
                  <><br />Futures API: api.Long(qty, leverage), api.Short(qty, leverage),
                  api.LimitLong(qty, price, leverage), api.LimitShort(qty, price, leverage),
                  api.CloseLong(qty), api.CloseShort(qty)</>
                )}
              </span>
            </div>
          )}

          {isScriptedRunner && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Configuration (JSON) - Optional</label>
              <textarea
                value={configJson}
                onChange={(e) => setConfigJson(e.target.value)}
                style={{ ...styles.input, ...styles.textarea }}
                placeholder='{"myParam": 100}'
                rows={4}
              />
              <span style={styles.hint}>
                Optional JSON config accessible in script via the &apos;config&apos; global object
              </span>
            </div>
          )}

          <div style={styles.actions}>
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                style={styles.deleteButton}
              >
                Delete
              </button>
            )}
            <button
              type="submit"
              style={styles.submitButton}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Runner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a2e',
    padding: '32px',
  },
  loading: {
    color: '#ccd6f6',
    textAlign: 'center',
    padding: '48px',
  },
  card: {
    backgroundColor: '#16213e',
    padding: '32px',
    borderRadius: '12px',
    maxWidth: '900px',
    margin: '0 auto',
    border: '1px solid #233554',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    color: '#ccd6f6',
    fontSize: '24px',
    margin: 0,
  },
  backButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #8892b0',
    backgroundColor: 'transparent',
    color: '#8892b0',
    cursor: 'pointer',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  row: {
    display: 'flex',
    gap: '16px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
  },
  label: {
    color: '#ccd6f6',
    fontSize: '14px',
  },
  input: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #233554',
    backgroundColor: '#0f3460',
    color: '#ccd6f6',
    fontSize: '16px',
    outline: 'none',
  },
  textarea: {
    resize: 'vertical' as const,
    fontFamily: 'monospace',
    fontSize: '14px',
  },
  hint: {
    color: '#8892b0',
    fontSize: '12px',
  },
  error: {
    backgroundColor: 'rgba(233, 69, 96, 0.2)',
    border: '1px solid #e94560',
    color: '#e94560',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '14px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '16px',
  },
  submitButton: {
    padding: '14px 28px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#e94560',
    color: 'white',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  deleteButton: {
    padding: '14px 28px',
    borderRadius: '8px',
    border: '1px solid #e94560',
    backgroundColor: 'transparent',
    color: '#e94560',
    fontSize: '16px',
    cursor: 'pointer',
  },
  noCredentials: {
    color: '#8892b0',
    fontSize: '14px',
    padding: '12px',
    backgroundColor: 'rgba(35, 53, 84, 0.5)',
    borderRadius: '8px',
  },
  link: {
    color: '#e94560',
    textDecoration: 'none',
  },
};
